// modules
import React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { Link, NavLink, withRouter } from 'react-router-dom';
import { withTranslation } from 'react-i18next';
import styled from 'styled-components';
import media from 'styled-media-query';
import ReactHtmlParser from 'react-html-parser';
// redux
import { setLocale } from 'store/Config';
// assets
import background from 'assets/images/sidebar_bg.png';
// styles
import { Divider, Dropdown, Menu, Sidebar } from 'semantic-ui-react';

const StyledSidebarMenuWrapper = styled.div`
	.ui.sidebar {
		background-image: ${(props) => `url(${props.background})`};
		background-position: top;
		background-repeat: no-repeat;
		background-size: cover;
	}

	${media.lessThan('400px')`
		.ui.sidebar {
			width: 320px;
			padding: 16px;
		}
	`}

	${media.between('400px', '600px')`
		.ui.sidebar {
			width: 400px;
			padding: 20px;
		}
	`}

	${media.between('600px', '800px')`
		.ui.sidebar {
			width: 520px;
			padding: 24px;
		}
	`}

	${media.greaterThan('800px')`
		.ui.sidebar {
			width: 600px;
			padding: 32px;
		}
	`}
`;

const StyledCrossButton = styled.svg`
    position: absolute;
    cursor: pointer;
    top: 24px;
    right: 24px;
    width: 24px;
    height: 24px;
    color: #ffffff;

    &:hover {
        opacity: 0.5;
    }
`;

const StyledCourseLanguageSelect = styled.div`
	color: #fff;

	${media.lessThan('400px')`
		margin-bottom: 8px;
	`}

	${media.between('400px', '600px')`
		margin-bottom: 12px;
	`}

	${media.greaterThan('600px')`
		margin-bottom: 16px;
	`}
`;

const StyledLinksList = styled.div`
    display: flex;
    flex-direction: column;
`;

const StyledLink = styled(Link)`
    cursor: pointer;
    color: #dbdbdb;
    font-weight: 400;

    :hover {
		color: #ffffff;
	}

	${media.lessThan('400px')`
		margin-bottom: 8px;
        font-size: 14px;
	`}

	${media.between('400px', '600px')`
		margin-bottom: 12px;
        font-size: 16px;
	`}

	${media.greaterThan('600px')`
		margin-bottom: 16px;
        font-size: 18px;
	`}
`;

const StyledLessonLink = styled.div`
	line-height: 1.4;

	a {
		pointer-events: ${(props) => (props.isAvailable ? 'auto' : 'none')};
		color: ${(props) => (props.isAvailable ? '#dbdbdb' : '#838383')};
	}

	a.active {
		font-weight: 600;
		color: #ffffff;
	}

	a:hover {
		color: #ffffff;
	}

	${media.lessThan('400px')`
		margin-bottom: 4px;
		font-size: 12px;
	`}

	${media.between('400px', '600px')`
		margin-bottom: 8px;
		font-size: 14px;
	`}

	${media.greaterThan('600px')`
		margin-bottom: 12px;
		font-size: 16px;
	`}
`;

const SidebarMenu = ({
    changeLocale,
    currentCourse,
    locale,
    open,
    t,
    toggleSidebarMenu,
}) => {
    const lessonLinks = currentCourse && currentCourse.lessons && Array.isArray(currentCourse.lessons)
        ? currentCourse.lessons.map((item, index) => (
            <StyledLessonLink
                key={`sidebar-menu-item-${index}`}
                isAvailable={item.isAvailable}
            >
                <NavLink
                    activeClassName='active'
                    to={item.to}
                    onClick={() => toggleSidebarMenu(false)}
                >
                    <span className='section'>
                        {item.section ? `${item.section}. ` : item.section}
                    </span>
                    <span>{ReactHtmlParser(item.displayName || item.name)}</span>
                </NavLink>
            </StyledLessonLink>
        )) : [];
    const sidebarBackground = currentCourse && currentCourse.courseStyle && currentCourse.courseStyle.sidebarBackground ? currentCourse.courseStyle.sidebarBackground : background;

    return (
        <StyledSidebarMenuWrapper background={sidebarBackground}>
            <Sidebar
                as={Menu}
                animation='overlay'
                direction='right'
                inverted
                vertical
                onHide={() => toggleSidebarMenu(false)}
                visible={open}
            >
                <StyledCrossButton
                    version='1.1'
                    xmlns='http://www.w3.org/2000/svg'
                    onClick={() => toggleSidebarMenu(false)}
                >
                    <line
                        x1='0'
                        y1='0'
                        x2='24'
                        y2='24'
                        stroke='#ffffff'
                        strokeWidth='2'
                    />
                    <line
                        x1='0'
                        y1='24'
                        x2='24'
                        y2='0'
                        stroke='#ffffff'
                        strokeWidth='2'
                    />
                </StyledCrossButton>
                <StyledCourseLanguageSelect>
                    <Dropdown
                        inline
                        selectOnBlur={false}
                        text={t('interfaceLanguage')}
                        value={locale}
                        options={[
                            { key: 'kk', text: t('kk'), value: 'kk' },
                            { key: 'ru', text: t('ru'), value: 'ru' },
                        ]}
                        onChange={(e, { value }) => changeLocale(value)}
                    />
                </StyledCourseLanguageSelect>
                <Divider />
                <StyledLinksList>
                    <StyledLink to='/' onClick={() => toggleSidebarMenu(false)}>
                        {t('landing.title')}
                    </StyledLink>
                    {currentCourse && currentCourse.link && (
                        <StyledLink to={currentCourse.link} onClick={() => toggleSidebarMenu(false)}>
                            {t('dashboard.course.visualization.header')}
                        </StyledLink>
                    )}
                </StyledLinksList>
                <Divider />
                <StyledLinksList>{lessonLinks}</StyledLinksList>
            </Sidebar>
        </StyledSidebarMenuWrapper>
    );
};

const mapStateToProps = (state) => ({
    currentCourse: state.ui.currentCourse,
    locale: state.config.locale,
});

const mapDispatchToProps = (dispatch) => ({
    changeLocale: (locale) => {
        dispatch(setLocale(locale));
        window.location.reload();
    },
});

export default withRouter(
    compose(
        connect(mapStateToProps, mapDispatchToProps),
        withTranslation(),
    )(SidebarMenu)
);
